import React, { createContext, useContext, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor";
import { isMobile } from "react-device-detect";
import { InAppPurchase2 as iap } from "@ionic-native/in-app-purchase-2";
import {
  MONTLY_SUBSCRIPTION,
  PRESEASON_SUBSCRIPTION,
  YEARLY_SUBSCRIPTION,
} from "../../Components/Subscription/PaymentMethod/constant";
import { IonAlert, IonLoading } from "@ionic/react"; // Added IonLoading import
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase.config";
import { actions } from "../../Redux/store";
import { usePaywallStore } from "../../zustandStore/paywallStore";
import { useMixpanel } from "../MixpanelContext/MixpanelProvider";
import { useBookaCallStore } from "../../zustandStore/bookaCallStore";

const API_KEY = "appl_ZzZixHLQdFcItFOXOsukueILBMx";
const platform = Capacitor.getPlatform();

interface AuthContextProp {
  children: React.ReactNode;
}

interface AuthContextValue {
  products: any[];
  isLoading: boolean;
  error: string | null;
  customerInfo: any;
  isSubscribed: boolean;
  checkSubscriptionStatus: () => Promise<void>;
  refreshCustomerInfo: () => Promise<void>;
  syncOldPurchases: () => Promise<void>;
  platform: string | null | undefined;
  purchaseProduct: (
    userProfile: any,
    userId: string,
    productId: string,
  ) => Promise<any>;
  showAlert: (header: string, message: string, buttons: any[]) => void;
  monthlySubscription: boolean;
  yearlySubscription: boolean;
  preseasonSubscription: boolean; // Add this line
  manualRestorePurchases: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export const RevenueCatContextProvider: React.FC<AuthContextProp> = ({
  children,
}) => {
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<any>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showAlertState, setShowAlertState] = useState(false);
  const [monthlySubscription, setMonthlySubscription] = useState(false);
  const [yearlySubscription, setYearlySubscription] = useState(false);
  const [preseasonSubscription, setPreseasonSubscription] = useState(false);
  // Add a specific state for purchase loading
  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
  // Add loading message state
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const { setHasSeenPaywall } = usePaywallStore();
  const navigate = useNavigate();
  const Mixpanel = useMixpanel();
  const { incrementCount } = useBookaCallStore();
  const [alertProps, setAlertProps] = useState({
    header: "",
    message: "",
    buttons: [{ text: "OK" }],
  });

  console.log(products, "products");
  console.log(customerInfo, "customerInfo");
  console.log(isSubscribed, "isSubscribed");
  console.log(isLoading, "isLoading");
  console.log(error, "error");

  const user =
    JSON.parse(localStorage.getItem("auth-token") || "{}")?.state?.user ?? null;
  const userId = user?.user?.id || user?.id || null;

  // Function to show alert
  const showAlert = (
    header: string,
    message: string,
    buttons: any[] = [{ text: "OK" }],
  ) => {
    setAlertProps({
      header,
      message,
      buttons,
    });
    setShowAlertState(true);
  };

  useEffect(() => {
    const setup = async () => {
      setIsLoading(true);
      setLoadingMessage("Setting up payment system...");
      try {
        const availableProducts = await initializeRevenueCat(userId);
        setProducts(availableProducts);
        setError(null);
        await syncOldPurchases();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
        setError(errorMessage);
        setProducts([]);

        // Show alert for initialization error
        if (platform === "ios") {
          showAlert(
            "Setup Error",
            `There was a problem initializing payments: ${errorMessage}`,
            [{ text: "OK" }],
          );
        }
      } finally {
        setIsLoading(false);
      }
    };
    setup();
  }, [userId]);

  const syncOldPurchases = async () => {
    if (!userId) {
      setError("User must be logged in to sync purchases");
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage("Syncing previous purchases...");
      console.log("Starting purchase sync process...");

      // Step 1: Check for old subscriptions first
      let oldSubscriptionDetected = false;
      let oldSubscriptionType = null;

      // Promise-based check for old subscriptions with proper error handling
      const checkOldSubscription = () => {
        return new Promise<{ isSubscribed: boolean; type: string | null }>(
          (resolve, reject) => {
            if (isMobile) {
              console.log("Checking old subscriptions via IAP...");

              // Initialize tracking variables
              let monthlyChecked = false;
              let yearlyChecked = false;

              // Set up error handling and timeout
              const checkTimeout = setTimeout(() => {
                if (!monthlyChecked || !yearlyChecked) {
                  console.warn("IAP subscription check timed out");
                  resolve({ isSubscribed: false, type: null });
                }
              }, 5000); // Increased timeout for slower devices

              try {
                // Refresh store and register callbacks
                iap.refresh();

                // Setup handlers for monthly subscription
                iap
                  .when(MONTLY_SUBSCRIPTION)
                  .updated((product: any) => {
                    console.log("Monthly subscription check result:", product);
                    monthlyChecked = true;

                    if (product.owned) {
                      console.log(
                        "Found active monthly subscription in old system",
                      );
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: true, type: "monthly" });
                    } else if (monthlyChecked && yearlyChecked) {
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: false, type: null });
                    }
                  })
                  .error((error: any) => {
                    console.error(
                      "Error checking monthly subscription:",
                      error,
                    );
                    monthlyChecked = true;
                    if (monthlyChecked && yearlyChecked) {
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: false, type: null });
                    }
                  });

                // Setup handlers for yearly subscription
                iap
                  .when(YEARLY_SUBSCRIPTION)
                  .updated((product: any) => {
                    console.log("Yearly subscription check result:", product);
                    yearlyChecked = true;

                    if (product.owned) {
                      console.log(
                        "Found active yearly subscription in old system",
                      );
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: true, type: "yearly" });
                    } else if (monthlyChecked && yearlyChecked) {
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: false, type: null });
                    }
                  })
                  .error((error: any) => {
                    console.error("Error checking yearly subscription:", error);
                    yearlyChecked = true;
                    if (monthlyChecked && yearlyChecked) {
                      clearTimeout(checkTimeout);
                      resolve({ isSubscribed: false, type: null });
                    }
                  });
              } catch (error) {
                console.error("Error during IAP check:", error);
                clearTimeout(checkTimeout);
                resolve({ isSubscribed: false, type: null });
              }
            } else {
              // Not on mobile, no subscriptions to check
              resolve({ isSubscribed: false, type: null });
            }
          },
        );
      };

      // Step 2: Check if user already has active subscriptions in RevenueCat
      console.log("Checking current RevenueCat subscription status");
      const currentInfo = await checkSubscriptionStatus();
      const hasActiveRevenueCatSubscription =
        currentInfo?.customerInfo?.activeSubscriptions &&
        currentInfo?.customerInfo?.activeSubscriptions.length > 0;

      console.log(
        "Current RevenueCat subscription status:",
        hasActiveRevenueCatSubscription ? "Active" : "Inactive",
      );

      // Step 3: Only proceed with old subscription check if no active RevenueCat subscription
      if (!hasActiveRevenueCatSubscription) {
        console.log("No active RevenueCat subscription, checking old system");
        const oldSubscriptionStatus = await checkOldSubscription();
        oldSubscriptionDetected = oldSubscriptionStatus.isSubscribed;
        oldSubscriptionType = oldSubscriptionStatus.type;

        console.log(
          "Old subscription check result:",
          oldSubscriptionDetected
            ? `Active (${oldSubscriptionType})`
            : "No active subscription",
        );

        // Step 4: Attempt to sync purchases if old subscription detected
        if (oldSubscriptionDetected) {
          console.log("Syncing old purchases with RevenueCat");

          // First, try RevenueCat's built-in sync
          await Purchases.syncPurchases();

          // Verify the sync was successful by refreshing customer info
          const updatedInfo = await Purchases.getCustomerInfo();
          console.log("Post-sync RevenueCat customer info:", updatedInfo);

          const syncSuccessful =
            updatedInfo?.customerInfo?.activeSubscriptions &&
            updatedInfo?.customerInfo?.activeSubscriptions.length > 0;

          if (syncSuccessful) {
            console.log(
              "Sync successful! Subscription transferred to RevenueCat",
            );
            await refreshCustomerInfo();

            if (platform === "ios") {
              showAlert(
                "Subscription Restored",
                `Your ${oldSubscriptionType} subscription has been successfully restored.`,
                [{ text: "Great!" }],
              );
            }
          } else {
            console.warn(
              "Initial sync failed, attempting alternative approach",
            );

            // If sync failed, try an alternative approach
            // This depends on your store and may require store-specific code
            if (platform === "ios") {
              // iOS-specific restore
              try {
                await Purchases.restorePurchases();

                // Check again if restore worked
                const restoredInfo = await Purchases.getCustomerInfo();
                const restoreSuccessful =
                  restoredInfo?.customerInfo?.activeSubscriptions &&
                  restoredInfo?.customerInfo?.activeSubscriptions.length > 0;

                if (restoreSuccessful) {
                  console.log(
                    "Restore successful! Subscription transferred to RevenueCat",
                  );
                  await refreshCustomerInfo();

                  showAlert(
                    "Subscription Restored",
                    `Your ${oldSubscriptionType} subscription has been successfully restored.`,
                    [{ text: "Great!" }],
                  );
                } else {
                  // Handle failed restore
                  console.error(
                    "Failed to restore subscription via both methods",
                  );
                  showAlert(
                    "Subscription Restore Failed",
                    "We detected you have an active subscription, but couldn't restore it automatically. Please contact support.",
                    [{ text: "OK" }],
                  );
                }
              } catch (restoreError) {
                console.error("Error during restorePurchases:", restoreError);
                showAlert(
                  "Restore Error",
                  "We encountered an error while restoring your subscription. Please try again later or contact support.",
                  [{ text: "OK" }],
                );
              }
            }
          }
        }
      } else {
        console.log(
          "User already has active RevenueCat subscription, skipping old subscription check",
        );
      }
    } catch (error) {
      console.error("Error in syncOldPurchases:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to sync purchases";
      setError(errorMessage);

      if (platform === "ios") {
        showAlert(
          "Sync Error",
          `Unable to restore previous purchases: ${errorMessage}`,
          [{ text: "OK" }],
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const info: any = await Purchases.getCustomerInfo();
      console.log("Customer info:", info);
      setCustomerInfo(info);

      const hasActiveSubscription =
        info?.customerInfo?.activeSubscriptions &&
        info?.customerInfo?.activeSubscriptions.length > 0;

      setIsSubscribed(hasActiveSubscription);

      if (hasActiveSubscription) {
        const activeSubscriptions = info?.customerInfo?.activeSubscriptions;
        const monthlySubscription =
          activeSubscriptions.includes(MONTLY_SUBSCRIPTION);
        const yearlySubscription =
          activeSubscriptions.includes(YEARLY_SUBSCRIPTION);
        const preseasonSubscription = activeSubscriptions.includes(
          PRESEASON_SUBSCRIPTION,
        );

        setYearlySubscription(yearlySubscription);
        setMonthlySubscription(monthlySubscription);
        setPreseasonSubscription(preseasonSubscription);
      } else {
        setYearlySubscription(false);
        setMonthlySubscription(false);
        setPreseasonSubscription(false);
      }
      return info;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to check subscription status";
      setError(errorMessage);
      throw error;
    }
  };

  const refreshCustomerInfo = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Refreshing subscription information...");
      await checkSubscriptionStatus();
    } catch (error) {
      console.error("Error refreshing customer info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getProducts = async () => {
    try {
      const offerings = await Purchases.getOfferings();
      console.log("Offerings:", offerings);
      if (
        offerings?.current &&
        offerings.current.availablePackages?.length > 0
      ) {
        console.log("Available Packages:", offerings.current.availablePackages);
        return offerings.current.availablePackages;
      } else {
        console.warn("No available packages found.");

        if (platform === "ios") {
          showAlert(
            "No Products Available",
            "We couldn't find any subscription options. Please try again later.",
            [{ text: "OK" }],
          );
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      throw new Error("Failed to fetch products");
    }
    return [];
  };

  const initializeRevenueCat = async (userId: string | null) => {
    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      await Purchases.configure({
        apiKey: API_KEY,
        appUserID: userId,
      });

      await checkSubscriptionStatus();
      const availableProducts = await getProducts();
      console.log(
        "Available products initializeRevenueCat:",
        availableProducts,
      );

      return availableProducts;
    } catch (error) {
      console.error("Error initializing RevenueCat:", error);
      throw new Error("Failed to initialize RevenueCat");
    }
  };

  const purchaseProduct = async (
    userProfile: any,
    userId: string,
    productId: string,
  ) => {
    try {
      // Use the specific purchase loading state
      setIsPurchaseLoading(true);
      // Log the available products and the requested productId
      console.log("Available products:", products);
      console.log("Attempting to purchase product ID:", productId);

      const selectedProduct = products.find(
        (product) => product?.product?.identifier === productId,
      );
      if (!selectedProduct) {
        console.error("Product not found in available products");
        const errorMessage = `Product ${productId} not found`;

        if (platform === "ios") {
          showAlert(
            "Product Not Found",
            "The selected subscription option is not available. Please try again.",
            [{ text: "OK" }],
          );
        }

        throw new Error(errorMessage);
      }

      console.log("Selected product for purchase:", selectedProduct);

      // Wrap the purchase in a try-catch to get more specific error information
      try {
        const purchaseInfo = await Purchases.purchasePackage({
          aPackage: selectedProduct,
        });

        console.log("Purchase successful, full response:", purchaseInfo);
        setIsPurchaseLoading(false);
        // Verify the purchase was successful
        if (purchaseInfo?.customerInfo?.activeSubscriptions?.length > 0) {
          console.log("Active subscription found after purchase");
          // Save subscription information to Supabase
          await saveAppleSubscription(userProfile, userId, purchaseInfo);
          // Increment BookaCall count after successful purchase
          incrementCount();

          if (platform === "ios") {
            if (userProfile?.hard_paywall) {
              Mixpanel.trackEvent("hard_paywall_purchase", {
                name: "Hard Paywall Purchase",
                purchaseInfo: purchaseInfo,
              });
            } else {
              Mixpanel.trackEvent("soft_paywall_purchase", {
                name: "Soft Paywall Purchase",
                purchaseInfo: purchaseInfo,
              });
            }
            actions.modal.closeAllModals(null);
            setHasSeenPaywall(true);
            navigate("/chatbot-ai");
            // showAlert(
            //   "Purchase Successful",
            //   `Thank you! Your ${subscriptionType} subscription is now active.`,
            //   [{ text: "Great!" }],
            // );
          }
        }

        await refreshCustomerInfo();
        return purchaseInfo;
      } catch (purchaseError: any) {
        setIsPurchaseLoading(false);
        // Log the specific error code and details from RevenueCat
        console.error("RevenueCat purchase error details:", {
          code: purchaseError.code,
          message: purchaseError.message,
          userCancelled: purchaseError.userCancelled,
          productIdentifier: purchaseError.productIdentifier,
        });

        // Handle user cancellation separately
        if (purchaseError.userCancelled) {
          if (platform === "ios") {
            showAlert(
              "Purchase Cancelled",
              "You cancelled the purchase. No payment was processed.",
              [{ text: "OK" }],
            );
          }
          throw new Error("Purchase was cancelled by user");
        }

        // Handle other specific error cases
        let errorMessage = "An error occurred during purchase";

        switch (purchaseError.code) {
          case "PurchasesErrorCode.purchaseNotAllowed":
            errorMessage = "Purchases are not allowed on this device";
            break;
          case "PurchasesErrorCode.storeProblem":
            errorMessage = "There was a problem with the App Store";
            break;
          case "PurchasesErrorCode.paymentPending":
            errorMessage = "Your payment is pending approval";
            break;
          case "PurchasesErrorCode.networkError":
            errorMessage =
              "Network connection error. Please check your internet connection.";
            break;
          case "PurchasesErrorCode.receiptAlreadyInUse":
            errorMessage =
              "This subscription is already active on another account";
            break;
          case "PurchasesErrorCode.invalidCredentials":
            errorMessage =
              "Invalid payment credentials. Please check your payment information.";
            break;
          default:
            errorMessage =
              purchaseError.message || "Unknown error occurred during purchase";
        }

        if (platform === "ios") {
          showAlert("Purchase Failed", errorMessage, [{ text: "OK" }]);
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      setIsPurchaseLoading(false);
      console.error("Error during purchase:", error);
      // Don't show alert here as it's already handled in the inner try-catch
      setError(
        error instanceof Error
          ? `Purchase failed: ${error.message}`
          : "Purchase failed: Unknown error occurred",
      );
      throw error;
    } finally {
      // Use the specific purchase loading state
      setIsPurchaseLoading(false);
    }
  };

  const manualRestorePurchases = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Restoring purchases...");

      // We don't want to show an alert that can't be dismissed while processing
      // Instead, use the loading indicator to show progress

      console.log("Starting purchase restore process...");
      await Purchases.restorePurchases();
      const restoredInfo = await Purchases.getCustomerInfo();

      const restoreSuccessful =
        restoredInfo?.customerInfo?.activeSubscriptions &&
        restoredInfo?.customerInfo?.activeSubscriptions.length > 0;

      if (restoreSuccessful) {
        await refreshCustomerInfo();

        if (platform === "ios") {
          showAlert(
            "Restore Successful",
            "Your subscription has been restored successfully!",
            [{ text: "Great!" }],
          );
        }
      } else {
        if (platform === "ios") {
          showAlert(
            "No Subscriptions Found",
            "We couldn't find any active subscriptions linked to your account.",
            [{ text: "OK" }],
          );
        }
      }
    } catch (error) {
      console.error("Error restoring purchases:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      if (platform === "ios") {
        showAlert(
          "Restore Failed",
          `Failed to restore purchases: ${errorMessage}`,
          [{ text: "OK" }],
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveAppleSubscription = async (
    userProfile: any,
    userId: string,
    product: any,
  ) => {
    // const userProfile = await getUserProfile(userId);
    console.log("saveAppleSubscription => product", product);
    console.log(
      "saveAppleSubscription => JSON.stringify(product)",
      JSON.stringify(product),
    );

    if (userProfile) {
      const { data, error } = await supabase
        .from("profiles")
        .update({
          apple_subscription: JSON.stringify(product),
        })
        .eq("user_id", userId);

      if (error) {
        console.error("[UPDATE] error creating apple subscription", error);
      } else {
        console.log("[UPDATE] apple subscription saved", data);
      }
    } else {
      const { data, error } = await supabase.from("profiles").insert({
        user_id: userId,
        apple_subscription: JSON.stringify(product),
      });

      if (error) {
        console.error("[INSERT] error saving apple subscription", error);
      } else {
        console.log("[INSERT] apple subscription update", data);
      }
    }
  };

  const contextValue: AuthContextValue = {
    products,
    isLoading,
    error,
    customerInfo,
    isSubscribed,
    checkSubscriptionStatus,
    refreshCustomerInfo,
    syncOldPurchases,
    platform,
    purchaseProduct,
    showAlert,
    monthlySubscription,
    yearlySubscription,
    preseasonSubscription, // Add this line
    manualRestorePurchases,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {platform === "ios" && (
        <>
          <IonAlert
            isOpen={showAlertState}
            onDidDismiss={() => setShowAlertState(false)}
            header={alertProps.header}
            message={alertProps.message}
            buttons={alertProps.buttons}
          />
          {/* Add IonLoading component for purchase process */}
          <IonLoading
            isOpen={isPurchaseLoading}
            message={"Loading..."}
            spinner="circles"
          />
        </>
      )}
    </AuthContext.Provider>
  );
};

export const useRevenueCatAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      "useRevenueCatAuth must be used within a RevenueCatContextProvider",
    );
  }
  return context;
};
